import React, { useEffect, useRef, useMemo } from 'react';
import Wrapper from 'Overlay/Wrapper';
import { shallowEqual } from 'react-redux';
import loadable from '@loadable/component';
import Loading from 'components/Loading';
import useAppDispatch from '@fogia/core-web/hooks/useAppDispatch';
import { allow as allowScroll, disallow as disallowScroll } from '@fogia/core-web/state/application/scroll';
import { TransitionGroup, Transition } from 'react-transition-group';
import useAppSelector from '@fogia/core-web/hooks/useAppSelector';
import usePrevious from '@grebban/react-hooks/usePrevious';

// This is where we add new overlays
const overlayViews = {
    exampleOverlay: loadable(() => import('Overlay/views/Example'), { fallback: <Loading /> }),
    searchOverlay: loadable(() => import('Overlay/views/SearchOverlay'), { fallback: <Loading /> }),
    filterOverlay: loadable(() => import('Overlay/views/FilterOverlay'), { fallback: <Loading /> }),
    sortProductGridOverlay: loadable(() => import('Overlay/views/SortProductGridOverlay'), { fallback: <Loading /> }),
    categoriesOverlay: loadable(() => import('Overlay/views/CategoriesOverlay'), { fallback: <Loading /> }),
    productOverlay: loadable(() => import('Overlay/views/ProductOverlay'), { fallback: <Loading /> }),
    zoomGalleryOverlay: loadable(() => import('Overlay/views/ZoomGalleryOverlay'), { fallback: <Loading /> }),
    selectedLooksOverlay: loadable(() => import('Overlay/views/SelectedLooksOverlay'), { fallback: <Loading /> }),
    configurationOverlay: loadable(() => import('Overlay/views/ConfigurationOverlay'), { fallback: <Loading /> }),
    quoteOverlay: loadable(() => import('Overlay/views/QuoteOverlay'), { fallback: <Loading /> }),
};

// This is where we specify overlays with locked scroll
const overlayViewsWithLockedScroll = [
    'exampleOverlay',
    'filterOverlay',
    'searchOverlay',
    'filterOverlay',
    'sortProductGridOverlay',
    'categoriesOverlay',
    'productOverlay',
    'zoomGalleryOverlay',
    'selectedLooksOverlay',
    'configurationOverlay',
    'quoteOverlay',
];

// Overlay views that use OverlayPanel — these get fade on Wrapper + directional slide on Panel
const overlayPanelViews = new Set(['productOverlay', 'selectedLooksOverlay', 'configurationOverlay', 'quoteOverlay']);

const Overlay = () => {
    const dispatch = useAppDispatch();
    const view = useAppSelector(({ application }) => application?.overlay?.view || '');
    const transition = useAppSelector(({ application }) => application?.overlay?.transition || '');
    const data = useAppSelector(({ application }) => application?.overlay?.data || {}, shallowEqual);
    const previousView = usePrevious(view);
    const reference = useRef(null);

    // Get the content component that should be used for the active overlay
    const OverlayComponent = overlayViews?.[view];

    // Lock scroll if any of these overlay views are active
    const overlayViewHasLockedScroll = useMemo(() => overlayViewsWithLockedScroll.includes(view), [view]);

    // Handle "scrollable" state
    useEffect(() => {
        if (view) {
            if (overlayViewHasLockedScroll) dispatch(disallowScroll(view));
        } else if (overlayViewsWithLockedScroll.includes(previousView)) {
            dispatch(allowScroll(previousView));
        }
    }, [overlayViewHasLockedScroll, previousView, view, dispatch]);

    return (
        <TransitionGroup>
            {OverlayComponent && (
                <Transition nodeRef={reference.current} in={!!OverlayComponent} timeout={{ enter: 0, exit: 1000 }}>
                    {(transitionState) => {
                        const showing = transitionState === 'entered';
                        const usesOverlayPanel = overlayPanelViews.has(view);

                        return (
                            <Wrapper
                                top={
                                    view === 'searchOverlay' && [
                                        'var(--header-height)',
                                        null,
                                        null,
                                        null,
                                        null,
                                        'var(--header-search-height, 0)',
                                    ]
                                }
                                ref={reference}
                                showing={showing}
                                transition={usesOverlayPanel ? '' : transition}
                            >
                                <OverlayComponent
                                    {...data}
                                    {...(usesOverlayPanel && { showing, panelTransition: transition })}
                                />
                            </Wrapper>
                        );
                    }}
                </Transition>
            )}
        </TransitionGroup>
    );
};

export default Overlay;
